import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2, LogOut, ShieldCheck, Mail, Crown, User as UserIcon } from 'lucide-react';
import { apiService } from "../api/apiclient";

const PROVIDER_MAP = {
    APP_STORE: "Apple Store",
    STRIPE: "Stripe",
    GOOGLE_PLAY: "Google Play", // 未来扩展无压力
};

const ProfilePage = () => {
    const [user, setUser] = useState(null);

    useEffect(() => {
        const initUser = async () => {
            const localUser = localStorage.getItem('user');
            if (localUser) {
                try {
                    const sessionData = JSON.parse(localUser);
                    setUser(sessionData);
                } catch (e) {
                    console.error("Failed to parse local session", e);
                }
            }
            try {
                const remoteUser = await apiService.getCurrentUser();
                if (remoteUser) {
                    setUser(prev => ({ ...prev, ...remoteUser.user }));
                    localStorage.setItem('user', JSON.stringify({
                        ...(JSON.parse(localUser || '{}')),
                        ...remoteUser.user
                    }));
                }
            } catch (error) {
                console.error("Failed to sync user info from server", error);
                if (error.status === 401) {
                    window.location.href('/signin');
                }
            }
        };

        initUser();
    }, []);

    console.log(user);
    const handleLogout = () => {
        localStorage.removeItem('session');
        localStorage.removeItem('sessionId');
        window.location.href = '/signin';
    };

    if (!user) {
        return <div className="p-8 text-center text-gray-500 text-lg">Loading Profile...</div>;
    }

    return (
        <div className="flex-1 p-4 md:p-8 bg-gray-50 min-h-full">
            {/* Header Section */}
            <header className="flex justify-between items-center mb-10">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
                    <p className="text-gray-500">Manage your profile and subscription</p>
                </div>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white border border-red-100 text-red-600 rounded-xl shadow-sm hover:bg-red-50 transition-all font-medium"
                >
                    <LogOut size={18} /> Logout
                </button>
            </header>

            <div className="max-w-4xl grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center">
                        <div className="relative group">
                            <img
                                src={user.picture}
                                alt={user.name}
                                className="w-32 h-32 rounded-full border-4 border-white shadow-md mb-4 bg-gray-100 object-cover"
                            />
                            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer mb-4">
                                <PlusCircle className="text-white" size={24} />
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">{user.name}</h2>
                        <span className={`mt-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${user.plan === 'free' ? 'bg-gray-100 text-gray-500' : 'bg-red-50 text-[#bd1f1f]'
                            }`}>
                            {user.plan} Plan
                        </span>
                        <div className="w-full h-px bg-gray-50 my-6" />
                        {user.quota && <div className="w-full space-y-3">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-400 font-medium">Monthly Usage</span>
                                <span className="text-gray-900 font-bold">
                                    {user.quota?.posts_used} <span className="text-gray-300 font-normal">/ {user.quota?.limit}</span>
                                </span>
                            </div>
                            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-700 ease-out rounded-full ${(user.quota?.posts_used / user.quota?.limit) > 0.9 ? 'bg-red-500' : 'bg-red-600'
                                        }`}
                                    style={{ width: `${Math.min((user.quota?.posts_used / user.quota?.limit) * 100, 100)}%` }}
                                />
                            </div>
                            <p className="text-[11px] text-gray-400">
                                {user.quota.limit - user.quota.posts_used} posts remaining this month
                            </p>
                        </div>}
                        {user.plan === 'free' && (
                            <button className="mt-6 w-full py-3 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-black transition-colors shadow-lg shadow-gray-200">
                                Upgrade to Pro
                            </button>
                        )}
                    </div>

                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-400 uppercase mb-4 tracking-widest">Support</h3>
                        <p className="text-sm text-gray-600 mb-4">Need help with your {user.plan} account?</p>
                        <a
                            href="mailto:support@antaressocial.com"
                            className="w-full py-2 bg-gray-900 text-white rounded-xl hover:bg-black transition-colors flex justify-center items-center"
                        >
                            Contact Support
                        </a>

                    </div>
                </div>

                <div className="md:col-span-2 space-y-8">
                    <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-6 border-b border-gray-50 pb-4">
                            <UserIcon className="text-[#bd1f1f]" size={20} />
                            <h3 className="text-lg font-bold">Personal Information</h3>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                    <Mail size={18} className="text-gray-400" />
                                    <span className="text-gray-800 font-medium">{user.email}</span>
                                    <ShieldCheck size={16} className="text-green-500 ml-auto" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Display Name</label>
                                <input
                                    type="text"
                                    defaultValue={user.name}
                                    className="w-full p-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-[#bd1f1f] outline-none transition-all"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
                        <Crown className="absolute -right-4 -bottom-4 text-gray-50 opacity-50" size={120} />

                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-6 border-b border-gray-50 pb-4">
                                <Crown className="text-amber-500" size={20} />
                                <h3 className="text-lg font-bold">Subscription</h3>
                            </div>

                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-2xl font-black text-gray-900 capitalize">{user.plan} Plan</p>
                                    <p className="text-sm text-gray-500 mt-1">Status: <span className="text-green-600 font-medium uppercase">{user.subStatus === 'none' ? 'Active' : user.subStatus}</span></p>
                                    {user.subscription?.provider && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            Subscribe from {PROVIDER_MAP[user.subscription.provider] || user.subscription.provider} {user.subscription.environment}
                                        </p>
                                    )}
                                </div>
                                <button
                                    className="px-6 py-2 bg-[#bd1f1f] text-white rounded-xl shadow-md hover:bg-[#a01a1a] transition-all font-bold"
                                    onClick={() => {
                                        window.location.href = '/subscription';
                                    }}
                                >
                                    Upgrade Plan
                                </button>

                            </div>
                        </div>
                    </div>
                    <div className="pt-4">
                        <div className="bg-red-50 p-6 rounded-2xl border border-red-100">
                            <h4 className="text-red-800 font-bold mb-2">Danger Zone</h4>
                            <p className="text-red-600 text-sm mb-4">Deleting your account will permanently remove all your social media schedules and data.</p>
                            <button className="flex items-center gap-2 text-sm font-bold text-red-700 hover:text-red-900 transition-colors">
                                <Trash2 size={16} /> Delete Account
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;